/**
 * Utilidad centralizada para extraer y manejar errores de API
 * Soporta múltiples formatos de respuesta de error
 */

/**
 * Extrae el mensaje de error de una respuesta de API
 * @param {Error} error - Error de axios
 * @returns {string} Mensaje de error formateado
 */
export function extractErrorMessage(error) {
  // Sin respuesta del servidor (error de red/timeout)
  if (!error.response) {
    if (error.code === 'ECONNABORTED') {
      return 'Timeout: La solicitud tardó demasiado. Verifica tu conexión.'
    }
    if (error.message === 'Network Error') {
      return 'Error de red: Verifica tu conexión a internet.'
    }
    return error.message || 'Error desconocido de conexión'
  }

  const status = error.response.status
  const data = error.response.data

  // Error 401 - No autorizado
  if (status === 401) {
    return 'Sesión expirada. Por favor, vuelve a iniciar sesión.'
  }

  // Error 403 - Prohibido
  if (status === 403) {
    return 'No tienes permisos para realizar esta acción.'
  }

  // Error 400 - Solicitud inválida
  if (status === 400) {
    return extractValidationError(data)
  }

  // Error 404 - No encontrado
  if (status === 404) {
    return 'Recurso no encontrado.'
  }

  // Error 500+ - Error del servidor
  if (status >= 500) {
    if (typeof data === 'object' && data !== null) {
      if (data.detail) return data.detail
      if (data.error) return data.error
      if (data.message) return data.message
    }
    return 'Error interno del servidor. Intenta de nuevo más tarde.'
  }

  // Fallback
  return 'Error inesperado. Intenta de nuevo.'
}

/**
 * Extrae errores de validación de un error 400
 * @param {Object} data - Datos de error de la respuesta
 * @returns {string} Mensaje de error formateado
 */
function extractValidationError(data) {
  if (!data || typeof data !== 'object') {
    return 'Datos inválidos. Revisa los campos del formulario.'
  }

  // Si hay un mensaje de error directo
  if (data.detail) return data.detail
  if (data.error) return data.error
  if (data.message) return data.message

  // Si hay errores de campos específicos
  if (data.non_field_errors) {
    const errors = Array.isArray(data.non_field_errors)
      ? data.non_field_errors
      : [data.non_field_errors]
    return errors.join(', ')
  }

  // Buscar el primer error en cualquier campo
  const keys = Object.keys(data)
  for (const key of keys) {
    const fieldError = data[key]
    if (Array.isArray(fieldError)) {
      return fieldError.join(', ')
    } else if (typeof fieldError === 'string') {
      return fieldError
    }
  }

  // Fallback si no hay errores claros
  return 'Datos inválidos. Revisa los campos del formulario.'
}

/**
 * Determina el color del toast según el tipo de error
 * @param {Error} error - Error de axios
 * @returns {string} Color para el snackbar ('error', 'warning', 'info')
 */
export function getErrorColor(error) {
  if (!error.response) return 'warning'
  const status = error.response.status
  if (status === 401 || status === 403) return 'error'
  if (status === 400) return 'warning'
  if (status >= 500) return 'error'
  return 'error'
}

/**
 * Maneja un error de API y muestra notificación usando snackbar
 * @param {Error} error - Error de axios
 * @param {Object} store - Instancia de Vuex store
 */
export function handleApiError(error, store) {
  const message = extractErrorMessage(error)
  const color = getErrorColor(error)

  store.commit('snackbar_change_message', message)
  store.commit('snackbar_change_color', color)
  store.commit('snackbar_activate_true')

  console.error('[API Error]', error)
}

/**
 * Maneja un error de API y lo muestra en un modal de error
 * @param {Error} error - Error de axios
 * @param {Object} context - Contexto del componente (this)
 */
export function handleApiErrorInModal(error, context) {
  context.errores_guard = extractErrorMessage(error)
  context.head_color = 'red display-1 text--primary'
  context.dialog_errores = true
  console.error('[API Error]', error)
}
